// client/src/utils/notifications.ts
import { supabase } from "../lib/supabaseClient.ts";

// Import the VAPID public key from your environment variables
// Add .trim() to ensure no leading/trailing whitespace
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY?.trim();

/**
 * Converts a base64 string to a Uint8Array for use with the Push API.
 * @param base64String The base64 URL-safe encoded string.
 */
function urlBase64ToUint8Array(base64String: string) {
  // Ensure the string is valid before processing
  if (!base64String || typeof base64String !== 'string') {
    throw new Error("Invalid base64 string provided for conversion.");
  }

  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64); // Decode base64 string
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Requests notification permission and subscribes the user to push notifications.
 * Returns the subscription object if successful.
 */
export async function subscribeUserToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('Push messaging is not supported in your browser.');
    return;
    return;
  }

  // Add a check to ensure VAPID_PUBLIC_KEY is available and valid
  if (!VAPID_PUBLIC_KEY) {
    console.error("VAPID_PUBLIC_KEY is missing or invalid. Check your .env file.");
    console.error('Notifications setup failed: VAPID public key is missing.');
    return;
    return;
  }

  const reg = await navigator.serviceWorker.ready;
  const permission = await Notification.requestPermission();

  if (permission !== 'granted') {
    console.warn('Notification permission denied');
    return;
    return;
  }

  // Log the public key to verify it's loaded correctly
  console.log('VAPID public key:', VAPID_PUBLIC_KEY);

  try {
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    // Get the current authenticated user
    const user = (await supabase.auth.getUser()).data.user;
    const user_id = user?.id ?? null;

    // Save the subscription to Supabase
    const { error } = await supabase.from("push_subscriptions").insert([
      {
        user_id,
        subscription,
      },
    ]);
    if (error) {
      console.error('Error saving push subscription:', error);
      console.error('Failed to save push subscription to backend.');
    } else {
      console.info('Push notifications enabled and registered with backend!');
    }

    console.log('Push Subscription:', JSON.stringify(subscription));
    return subscription;
  } catch (err: any) { // Catch the specific error from pushManager.subscribe
    if (err.name === 'InvalidAccessError') {
      console.error('Push Subscription Error: Invalid applicationServerKey. Double-check your VAPID public key.', err);
      console.error('Failed to subscribe to push notifications: Invalid VAPID key.');
    } else if (err.name === 'NotAllowedError') {
        console.warn('Push Subscription Error: Permission denied or dismissed.', err);
        console.warn('Notifications permission denied or dismissed.');
    }
    else {
      console.error('Unexpected error during push subscription:', err);
      console.error('An unexpected error occurred while subscribing to push notifications.');
    }
    return null; // Return null on error
  }
}

/**
 * Unsubscribes the user from push notifications and removes their subscription from Supabase.
 * Returns true if unsubscribed, false otherwise.
 */
export async function unsubscribeUserFromPush(user: any) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      // Unsubscribe from the browser
      await subscription.unsubscribe();

      // Remove the subscription from Supabase
      if (user) {
        // Get the endpoint from the subscription object to ensure correct row is deleted
        const endpoint = subscription.endpoint;
        // Remove only the matching subscription for this user
        const { error } = await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", user.id)
          .eq("subscription->>endpoint", endpoint);

        if (error) {
          console.error('Error removing push subscription from backend:', error);
          console.error('Failed to remove push subscription from backend.');
        }
      }
      console.info('Push notifications disabled!');
      return true;
    }
  } catch (err) {
    console.error('Error during push unsubscription:', err);
    console.error('An unexpected error occurred while unsubscribing from push notifications.');
  }
  return false;
}

/**
 * Checks if the user is subscribed to push notifications.
 * Returns true if a subscription exists, false otherwise.
 */
export async function checkPushSubscription(): Promise<boolean> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}